/**
 * Keyboard copy mode for a terminal pane (Terminal board card 3, 21.10.2026),
 * as in tmux (prefix [) and WezTerm (ActivateCopyMode): a cursor of its own
 * walks the scrollback with vi keys or the arrows, `v` / `V` start a
 * character / line selection, `y` or Enter copies, Esc or `q` leaves.
 *
 * Pure: `copyModeKey(state, e, buf)` returns the next state and what the
 * caller must do. `buf` is a view of the xterm buffer:
 *   { lines: number, cols: number, rows: number, line(y) → string }
 *
 * state: { x, y, anchor: {x, y} | null, lineMode: boolean }
 */

// vi's `w` / `b`: letters, digits and _ make a word.
const isWordChar = (c) => /[\p{L}\p{N}_]/u.test(c || '');

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

/** Start where the shell's cursor is. */
export const startCopyMode = (cursorX, cursorY) => ({ x: cursorX, y: cursorY, anchor: null, lineMode: false });

/** Last column holding text on line `y` (0 for an empty line). */
const lineEnd = (buf, y) => Math.max(0, buf.line(y).replace(/\s+$/, '').length - 1);

/** Next word start after (x, y), continuing on the next lines. */
function nextWord(buf, x, y) {
  let s = buf.line(y);
  let i = x;
  while (i < s.length && isWordChar(s[i])) i += 1;
  for (;;) {
    while (i < s.length && !isWordChar(s[i])) i += 1;
    if (i < s.length) return { x: i, y };
    if (y >= buf.lines - 1) return { x: lineEnd(buf, y), y };
    y += 1;
    s = buf.line(y);
    i = 0;
  }
}

/** Start of the word before (x, y), continuing on the previous lines. */
function prevWord(buf, x, y) {
  let s = buf.line(y);
  let i = x - 1;
  for (;;) {
    while (i >= 0 && !isWordChar(s[i])) i -= 1;
    if (i >= 0) break;
    if (y <= 0) return { x: 0, y: 0 };
    y -= 1;
    s = buf.line(y);
    i = s.length - 1;
  }
  while (i > 0 && isWordChar(s[i - 1])) i -= 1;
  return { x: i, y };
}

function move(state, buf, key, shift) {
  const { x, y } = state;
  const last = buf.lines - 1;
  switch (key) {
    case 'h':
    case 'ArrowLeft':
      return { x: Math.max(0, x - 1), y };
    case 'l':
    case 'ArrowRight':
      return { x: Math.min(buf.cols - 1, x + 1), y };
    case 'k':
    case 'ArrowUp':
      return { x, y: Math.max(0, y - 1) };
    case 'j':
    case 'ArrowDown':
      return { x, y: Math.min(last, y + 1) };
    case 'w':
      return nextWord(buf, x, y);
    case 'b':
      return prevWord(buf, x, y);
    case '0':
    case 'Home':
      return { x: 0, y };
    case '$':
    case 'End':
      return { x: lineEnd(buf, y), y };
    case 'g':
      return shift ? null : { x: 0, y: 0 };
    case 'G':
      return { x: 0, y: last };
    case 'PageUp':
      return { x, y: Math.max(0, y - buf.rows) };
    case 'PageDown':
      return { x, y: Math.min(last, y + buf.rows) };
    default:
      return null;
  }
}

/**
 * One keydown in copy mode → `{ state, action }`; action is
 * 'copy' (copy the selection, then leave), 'exit', 'consumed' or null for
 * a key copy mode does not use (still kept from the shell by the caller).
 */
export function copyModeKey(state, e, buf) {
  if (e.ctrlKey || e.metaKey || e.altKey) return { state, action: null };
  const k = e.key;
  if (k === 'Escape' || k === 'q') return { state, action: 'exit' };
  if (k === 'y' || k === 'Enter') return { state, action: state.anchor ? 'copy' : 'exit' };
  if (k === 'v' || k === 'V') {
    const lineMode = k === 'V';
    if (state.anchor && state.lineMode === lineMode) return { state: { ...state, anchor: null, lineMode: false }, action: 'consumed' };
    return { state: { ...state, anchor: state.anchor || { x: state.x, y: state.y }, lineMode }, action: 'consumed' };
  }
  const to = move(state, buf, k, e.shiftKey && k === 'g');
  if (!to) return { state, action: null };
  const y = clamp(to.y, 0, buf.lines - 1);
  return { state: { ...state, x: clamp(to.x, 0, buf.cols - 1), y }, action: 'consumed' };
}

/**
 * What to select for `state`: { lines: [from, to] } for a line selection, or
 * { col, row, length } (xterm `select` arguments) — a one-cell selection
 * stands for the cursor while nothing is selected yet.
 */
export function copyModeSelection(state, cols) {
  const { x, y, anchor, lineMode } = state;
  if (!anchor) return { col: x, row: y, length: 1 };
  if (lineMode) return { lines: [Math.min(anchor.y, y), Math.max(anchor.y, y)] };
  const before = anchor.y < y || (anchor.y === y && anchor.x <= x);
  const [a, b] = before ? [anchor, { x, y }] : [{ x, y }, anchor];
  return { col: a.x, row: a.y, length: (b.y - a.y) * cols + (b.x - a.x) + 1 };
}

/**
 * The output of the last finished command: the lines between the last two
 * prompt marks (OSC 133;A lines), without either prompt line. `prompts` are
 * buffer line numbers in order; null when there are fewer than two.
 */
export function lastOutputRange(prompts) {
  const marks = (prompts || []).filter((n) => Number.isInteger(n) && n >= 0);
  if (marks.length < 2) return null;
  const end = marks[marks.length - 1] - 1;
  const start = marks[marks.length - 2] + 1;
  return end >= start ? [start, end] : null;
}
